import camelcaseKeys from 'camelcase-keys'
import { create } from 'zustand'

import { executeDelete, executeGet, executePost } from '@/services/lukita'
import {
  addOrUpdateItems,
  batch,
  getIds,
  removeItems,
  updateItem,
} from '@/utils'
import { DOCUMENT_STATUSES } from '../constants'
import { isDocumentToStart } from '../status'
import type { Document, DocumentDTO, DocumentsStore } from './types'

export const useDocumentsStore = create<DocumentsStore>((set, get, store) => ({
  loadingDocuments: true,
  loadingSearch: false,
  mutating: false,
  folderId: null,
  documents: [],

  actions: {
    startLoadingDocuments: () => set({ loadingDocuments: true }),
    stopLoadingDocuments: () => set({ loadingDocuments: false }),

    reset: () => set(store.getInitialState()),

    setDocuments: (documents) => set({ documents: camelcaseKeys(documents) }),

    setDocumentsStatus: (ids, status) =>
      set((state) => ({
        documents: state.documents.map((document) => {
          if (ids.includes(document.id)) {
            return { ...document, status }
          }
          return document
        }),
      })),

    addDocuments: (documents) =>
      set((state) => ({
        documents: addOrUpdateItems(state.documents, camelcaseKeys(documents)),
      })),

    addDocument: (document) =>
      get().actions.addDocuments([document] as Document[] | DocumentDTO[]),

    setDocument: (document) =>
      set((state) => ({
        documents: updateItem(state.documents, camelcaseKeys(document)),
      })),

    deleteDocuments: (ids) =>
      set((state) => ({
        documents: removeItems(state.documents, ids),
      })),
  },

  mutations: {
    ocrDocuments: async (folderId, ids) => {
      const { setDocumentsStatus } = get().actions

      const unprocessedIds: number[] = ids
        ? ids
        : get()
            .documents.filter((document) => isDocumentToStart(document))
            .map(({ id }) => id)

      setDocumentsStatus(unprocessedIds, DOCUMENT_STATUSES.enqueued)
      const path = `/documents/ocr`
      const body = ids ? { document_ids: ids } : {}

      try {
        await executePost(path, { ...body, folder_id: folderId })
      } catch (error) {
        setDocumentsStatus(unprocessedIds, DOCUMENT_STATUSES.notStarted)
        throw error
      }
    },

    deleteDocuments: async (documents) => {
      const { deleteDocuments, addDocuments } = get().actions

      const ids = getIds(documents)
      deleteDocuments(ids)

      try {
        await executeDelete(`/documents`, { ids })
      } catch (error) {
        addDocuments(documents)
        throw error
      }
    },

    createDocuments: async (blobs, folderId) => {
      set({ mutating: true })
      const hasFiles = blobs.length > 0
      if (hasFiles) {
        const blobsIds = blobs.map(({ signed_id }) => signed_id)
        const batches = batch(blobsIds, 5)

        const path = `/documents`
        const requests = batches.map(async (blobs_ids) => {
          const response = await executePost(path, {
            blobs_ids,
            folder_id: folderId,
          })
          return response.json()
        })

        try {
          const responses = await Promise.all(requests)
          get().actions.addDocuments(responses.flat())
        } catch (error) {
          set({ mutating: false })
          throw error
        }
      }
      set({ mutating: false })
    },
  },

  loaders: {
    fetchDocuments: async (folderId = null) => {
      const { folderId: storedFolderId, documents } = get()
      const { startLoadingDocuments, setDocuments, stopLoadingDocuments } =
        get().actions

      if (storedFolderId !== folderId || documents.length === 0) {
        startLoadingDocuments()
      }

      const response = await executeGet(`/documents`, {
        queryParams: folderId ? { folder_id: folderId } : {},
      })
      const result = await response.json()

      setDocuments(result, folderId)
      stopLoadingDocuments()
    },

    searchDocuments: async (q, folderId) => {
      const searchQuery = q.trim()
      set({ loadingSearch: true })

      if (searchQuery.length === 0) {
        set({ loadingSearch: false })
        return []
      }
      const queryParams = { q: searchQuery } as {
        q: string
        folder_id?: number
      }
      if (folderId) queryParams.folder_id = folderId

      const response = await executeGet(`/documents/search`, { queryParams })
      const result = await response.json()
      set({ loadingSearch: false })

      return camelcaseKeys(result.blocks, { deep: true })
    },
  },
}))
